// Tidssone-oversetting mellom det brukeren skriver og det API-et lagrer.
//
// KONTRAKTEN (services/dugnad/API.md): «Tidspunkt er alltid millisekunder siden
// epoch (…TsMs), aldri lokal streng. Tidssone-tolkning hører i klienten, ikke i
// lageret.» Denne fila ER den tolkningen, og derfor er den ren og testet: den
// rører verken DOM eller nettverk.
//
// HVORFOR IKKE `new Date('2026-07-15T18:30')`:
// Den tolker strengen i NETTLESERENS tidssone. En stabsoffiser som fører timer
// fra en maskin satt til UTC ville da fått føringen lagt to timer feil om
// sommeren — og feilen er stille, for datoen ser riktig ut i skjemaet etterpå.
// Vi låser derfor eksplisitt til norsk tid og regner om via Intl, som har hele
// den historiske sommertid-tabellen.

/** Norsk tid. Én konstant, så ingen skjerm kan gjette en annen sone. */
export const TIDSSONE = 'Europe/Oslo';

const MIN_MS = 60_000;
const DØGN_MS = 24 * 60 * MIN_MS;

/** Maks varighet én økt kan meldes med — speiler `OktSchema.minutter` (24 t). */
export const MAKS_ØKT_MIN = 24 * 60;

interface LokaleDeler {
  aar: number; mnd: number; dag: number; time: number; min: number; sek: number;
}

const FORMATERE = new Map<string, Intl.DateTimeFormat>();
function deleFormat(sone: string): Intl.DateTimeFormat {
  let f = FORMATERE.get(sone);
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', {
      timeZone: sone, hour12: false,
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
    FORMATERE.set(sone, f);
  }
  return f;
}

/** Veggklokka i `sone` på et gitt UTC-tidspunkt. */
export function lokaleDeler(utcMs: number, sone: string = TIDSSONE): LokaleDeler {
  const p: Record<string, string> = {};
  for (const d of deleFormat(sone).formatToParts(new Date(utcMs))) {
    if (d.type !== 'literal') p[d.type] = d.value;
  }
  // Noen ICU-versjoner skriver midnatt som «24» med hour12:false. Uten %24 ville
  // en økt meldt kl. 00:15 blitt lest som kl. 24:15 og havnet et døgn feil.
  return {
    aar: Number(p.year), mnd: Number(p.month), dag: Number(p.day),
    time: Number(p.hour) % 24, min: Number(p.minute), sek: Number(p.second),
  };
}

/**
 * Hvor mange millisekunder `sone` ligger FORAN UTC på det gitte UTC-tidspunktet.
 * Oslo: +1 t om vinteren, +2 t om sommeren.
 */
export function soneForskyvningMs(utcMs: number, sone: string = TIDSSONE): number {
  const d = lokaleDeler(utcMs, sone);
  const somOmDetVarUtc = Date.UTC(d.aar, d.mnd - 1, d.dag, d.time, d.min, d.sek);
  // Sammenlign på hele sekunder: `Date.UTC` over sekund-delene har ingen
  // millisekunder, så en ujevn `utcMs` ville ellers gitt en forskyvning på
  // f.eks. 3 599 137 ms i stedet for en hel time.
  return somOmDetVarUtc - Math.floor(utcMs / 1000) * 1000;
}

export interface FeltFeil { felt: string; grunn: string }

export type Tolket<T> = { ok: true; verdi: T; merknader: string[] } | { ok: false; feil: FeltFeil };

const DATO_RX = /^(\d{4})-(\d{2})-(\d{2})$/;
const KLOKKE_RX = /^(\d{1,2}):(\d{2})$/;

/** «2026-07-15» → tallene, med kontroll av at datoen finnes (32. juli gjør ikke). */
export function tolkDato(dato: string, felt = 'dato'): Tolket<{ aar: number; mnd: number; dag: number }> {
  const m = DATO_RX.exec(dato.trim());
  if (!m) return { ok: false, feil: { felt, grunn: 'oppgi dato som ÅÅÅÅ-MM-DD' } };
  const [aar, mnd, dag] = [Number(m[1]), Number(m[2]), Number(m[3])];
  // Rundturen fanger 2026-02-30 og 2026-13-01, som regexen slipper gjennom.
  const d = new Date(Date.UTC(aar, mnd - 1, dag));
  if (d.getUTCFullYear() !== aar || d.getUTCMonth() !== mnd - 1 || d.getUTCDate() !== dag) {
    return { ok: false, feil: { felt, grunn: `datoen ${dato} finnes ikke` } };
  }
  return { ok: true, verdi: { aar, mnd, dag }, merknader: [] };
}

/** «18:30» → minutter etter midnatt. */
export function tolkKlokke(klokke: string, felt = 'klokke'): Tolket<number> {
  const m = KLOKKE_RX.exec(klokke.trim());
  if (!m) return { ok: false, feil: { felt, grunn: 'oppgi klokkeslett som TT:MM' } };
  const t = Number(m[1]);
  const min = Number(m[2]);
  if (t > 23) return { ok: false, feil: { felt, grunn: 'timetallet må være 00–23' } };
  if (min > 59) return { ok: false, feil: { felt, grunn: 'minuttallet må være 00–59' } };
  return { ok: true, verdi: t * 60 + min, merknader: [] };
}

/**
 * Norsk veggklokke → UTC-millisekunder.
 *
 * To pass er nødvendig: forskyvningen avhenger av tidspunktet vi ennå ikke har
 * regnet ut. Første pass gjetter med forskyvningen slik den er på det UTC-punktet
 * som «ser ut som» veggklokka, andre pass korrigerer med forskyvningen der svaret
 * faktisk landet. Utenfor sommertid-helgene gir passene samme svar.
 */
export function lokalTidTilMs(
  dato: { aar: number; mnd: number; dag: number },
  minutterEtterMidnatt: number,
  sone: string = TIDSSONE,
): number {
  const antatt = Date.UTC(dato.aar, dato.mnd - 1, dato.dag)
    + minutterEtterMidnatt * MIN_MS;
  const ett = antatt - soneForskyvningMs(antatt, sone);
  return antatt - soneForskyvningMs(ett, sone);
}

export interface ØktWire {
  /** Det API-et skal ha: `POST /okter` → `startTsMs`. */
  startTsMs: number;
  /** Og `minutter` — regnet av den faktiske tidsdifferansen, ikke av klokketall. */
  minutter: number;
}

/**
 * Skjemaets tre felt (dato, fra, til i norsk tid) → `{ startTsMs, minutter }`.
 *
 * Varigheten regnes av MILLISEKUNDENE mellom de to tidspunktene, ikke av
 * differansen mellom klokketallene. Det er forskjellen som gjør at en vakt
 * 23:00–03:00 natta klokka stilles TILBAKE (25. oktober 2026) blir 5 timer og
 * ikke 4 — hun sto der en time lenger enn klokketallene sier, og det er den
 * tiden hun skal ha. Samme natta klokka stilles FRAM blir 01:00–04:00 to timer,
 * ikke tre, av nøyaktig samme grunn.
 */
export function tolkØktSkjema(
  dato: string,
  fraKlokke: string,
  tilKlokke: string,
  sone: string = TIDSSONE,
): Tolket<ØktWire> {
  const d = tolkDato(dato);
  if (!d.ok) return d;
  const fra = tolkKlokke(fraKlokke, 'fra');
  if (!fra.ok) return fra;
  const til = tolkKlokke(tilKlokke, 'til');
  if (!til.ok) return til;

  const merknader: string[] = [];
  const startTsMs = lokalTidTilMs(d.verdi, fra.verdi, sone);

  // Sluttid som er lik eller før starttid leses som «over midnatt». Det er den
  // vanligste ekte føringen vi har (vakt 22:00–02:00), og alternativet — å
  // avvise den — ville tvunget folk til å dele opp en økt de faktisk satt i ett.
  let sluttTsMs = lokalTidTilMs(d.verdi, til.verdi, sone);
  if (til.verdi <= fra.verdi) {
    const neste = new Date(Date.UTC(d.verdi.aar, d.verdi.mnd - 1, d.verdi.dag) + DØGN_MS);
    sluttTsMs = lokalTidTilMs(
      { aar: neste.getUTCFullYear(), mnd: neste.getUTCMonth() + 1, dag: neste.getUTCDate() },
      til.verdi,
      sone,
    );
    merknader.push(`Økta leses som at den går over midnatt og slutter ${tilKlokke} dagen etter.`);
  }

  const minutter = Math.round((sluttTsMs - startTsMs) / MIN_MS);
  if (minutter < 1) {
    return { ok: false, feil: { felt: 'til', grunn: 'sluttid må være etter starttid' } };
  }
  if (minutter > MAKS_ØKT_MIN) {
    return { ok: false, feil: { felt: 'til', grunn: 'én økt kan ikke være lengre enn 24 timer — del den opp' } };
  }

  // Sommertid-hullet: kl. 02:30 finnes ikke natta klokka stilles fram. Vi
  // avviser ikke — vi sier hva føringen faktisk ble, framfor å late som om
  // skjemaet ble lagret ordrett.
  const faktisk = lokaleDeler(startTsMs, sone);
  if (faktisk.time * 60 + faktisk.min !== fra.verdi) {
    merknader.push(
      `Klokka ${fraKlokke} finnes ikke denne datoen (sommertiden legges om) — `
      + `starten er lagret som ${toSiffer(faktisk.time)}:${toSiffer(faktisk.min)}.`,
    );
  }
  return { ok: true, verdi: { startTsMs, minutter }, merknader };
}

// ── Visning ─────────────────────────────────────────────────────────────────

function toSiffer(n: number): string { return String(n).padStart(2, '0'); }

/** «2026-07-15» i norsk tid — formen `<input type="date">` vil ha. */
export function somDatoFelt(tsMs: number, sone: string = TIDSSONE): string {
  const d = lokaleDeler(tsMs, sone);
  return `${d.aar}-${toSiffer(d.mnd)}-${toSiffer(d.dag)}`;
}

/** «18:30» i norsk tid — formen `<input type="time">` vil ha. */
export function somKlokkeFelt(tsMs: number, sone: string = TIDSSONE): string {
  const d = lokaleDeler(tsMs, sone);
  return `${toSiffer(d.time)}:${toSiffer(d.min)}`;
}

const MÅNEDER = [
  'januar', 'februar', 'mars', 'april', 'mai', 'juni',
  'juli', 'august', 'september', 'oktober', 'november', 'desember',
];
const DAGER = ['søn', 'man', 'tir', 'ons', 'tor', 'fre', 'lør'];

/**
 * «man. 27. juli» — norske navn skrevet ut for hånd framfor via `toLocaleString`.
 * Grunnen er ikke prinsipp, men at nb-NO-formatene varierer mellom nettlesere og
 * Node-versjoner, og en tabell som denne gir samme streng i test og i drift.
 */
export function formaterDato(tsMs: number, sone: string = TIDSSONE): string {
  const d = lokaleDeler(tsMs, sone);
  const uke = DAGER[new Date(Date.UTC(d.aar, d.mnd - 1, d.dag)).getUTCDay()];
  return `${uke}. ${d.dag}. ${MÅNEDER[d.mnd - 1]}`;
}

/** «man. 27. juli 2026» — med årstall, for datoer utenfor inneværende år. */
export function formaterDatoMedÅr(tsMs: number, sone: string = TIDSSONE): string {
  return `${formaterDato(tsMs, sone)} ${lokaleDeler(tsMs, sone).aar}`;
}

/** «man. 27. juli, 18:30» */
export function formaterDatoTid(tsMs: number, sone: string = TIDSSONE): string {
  return `${formaterDato(tsMs, sone)}, ${somKlokkeFelt(tsMs, sone)}`;
}

/** «18:30–20:50» — start pluss varighet, i norsk tid. */
export function formaterTidsrom(tsMs: number, minutter: number, sone: string = TIDSSONE): string {
  return `${somKlokkeFelt(tsMs, sone)}–${somKlokkeFelt(tsMs + minutter * MIN_MS, sone)}`;
}

/** Antall hele døgn mellom to tidspunkt, positivt når `b` er senere. */
export function døgnMellom(a: number, b: number): number {
  return Math.round((b - a) / DØGN_MS);
}
